import { MESSAGE_MAX, NICKNAME_MAX, type MemberView, type Stats } from "../shared/api";
import { FOUNDING_DEADLINE, QUESTION_COUNT } from "../shared/casting";
import { castingLines, typePairOf, type Role } from "../shared/content";
import { LOCALES, LOCALE_NAMES, isLocale } from "../shared/i18n";
import { renderCardImage, showCardPreview } from "./card-image";
import { handleShare, shareButtonsHtml } from "./share-buttons";
import { applyLocale, content, formatDate, getLocale, setLocale, t } from "./lang";

const app = document.querySelector<HTMLElement>("#app")!;
const memberChip = document.querySelector<HTMLAnchorElement>("#member-chip")!;
const langSelect = document.querySelector<HTMLSelectElement>("#lang-select");

const ANSWERS_KEY = "bm:answers";
const CHOICE_LABELS = ["A", "B", "C"];

// ===== 通信 =====

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    // サーバーからのエラーの文やメールを、いまの言語にしてもらう
    headers: { "Content-Type": "application/json", "X-Lang": getLocale(), ...init?.headers },
  });
  const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
  if (!res.ok) {
    throw Object.assign(new Error(body.error ?? t().networkError(res.status)), {
      status: res.status,
      code: body.code,
    });
  }
  return body as T;
}

const api = {
  me: () => request<MemberView>("/api/me"),
  signupStart: (email: string, nickname: string) =>
    request<{ ok: true }>("/api/signup/start", { method: "POST", body: JSON.stringify({ email, nickname }) }),
  signupVerify: (email: string, code: string) =>
    request<MemberView>("/api/signup/verify", { method: "POST", body: JSON.stringify({ email, code }) }),
  loginStart: (email: string) =>
    request<{ ok: true }>("/api/login/start", { method: "POST", body: JSON.stringify({ email }) }),
  loginVerify: (email: string, code: string) =>
    request<MemberView>("/api/login/verify", { method: "POST", body: JSON.stringify({ email, code }) }),
  logout: () => request<{ ok: true }>("/api/logout", { method: "POST" }),
  diagnose: (answers: number[]) =>
    request<MemberView>("/api/me/diagnosis", { method: "POST", body: JSON.stringify({ answers }) }),
  updateMessage: (message: string) =>
    request<MemberView>("/api/me", { method: "PATCH", body: JSON.stringify({ message }) }),
  updateLang: (lang: string) => request<MemberView>("/api/me", { method: "PATCH", body: JSON.stringify({ lang }) }),
  stats: () => request<Stats>("/api/stats"),
};

// ===== 表示の小道具 =====

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const memberNo = (n: number) => `No.${String(n).padStart(8, "0")}`;
const deadlineText = () => formatDate(FOUNDING_DEADLINE, false);

/** キャラの絵。元の絵から npm run figures で作った画像 */
const figure = (id: string) => `<img src="/img/chars/${id}.webp" width="400" height="476" alt="" decoding="async">`;

/** 名台詞: 原文 (英語) と、いまの言語のわかりやすい訳。英語のときは原文を上に */
function quoteHtml(quote: { original: string; translation: string }): string {
  const original = `<p lang="en" class="quote-original">${quote.original}</p>`;
  const translation = `<p class="quote-translation">${quote.translation}</p>`;
  return `<blockquote class="quote">${getLocale() === "en" ? original + translation : translation + original}</blockquote>`;
}

function show(html: string): void {
  app.innerHTML = html;
  window.scrollTo({ top: 0 });
  app.querySelector<HTMLElement>("[data-focus]")?.focus();
}

let me: MemberView | null = null;

function updateChip(): void {
  if (me?.diagnosis) {
    memberChip.hidden = false;
    memberChip.textContent = `${memberNo(me.number)} ${me.nickname}`;
  } else {
    memberChip.hidden = true;
  }
}

// ===== 1. トップ (登録前) =====

/** シェアされたリンク (/r/{役}) から来た人に見せる、その役の紹介 */
function sharedRoleTeaser(): string {
  const c = content();
  const id = location.pathname.match(/^\/r\/([a-z0-9]+)\/?$/)?.[1];
  const role = id ? c.role(id) : undefined;
  if (!role) return "";
  return `
    <section class="shared-role">
      <p class="shared-label">${t().sharedLabel}</p>
      <div class="shared-body">
        <div class="figure-frame is-small">${figure(role.id)}</div>
        <div>
          <p class="shared-name">${role.name}</p>
          <p class="shared-play">${c.playTitle(role.play)}</p>
          <p class="shared-types">${typePairOf(c, role.front, role.back)}</p>
        </div>
      </div>
      <p class="shared-text">${castingLines(c, role).join(getLocale() === "ja" ? "" : " ")}</p>
      <a class="btn btn-primary" href="#register">${t().findYours}</a>
    </section>`;
}

let landingState: { errorMessage?: string; values: { email: string; nickname: string }; errorCode?: string } = {
  values: { email: "", nickname: "" },
};

function renderLanding(
  errorMessage?: string,
  values = { email: "", nickname: "" },
  errorCode?: string,
): void {
  landingState = { errorMessage, values, errorCode };
  const ui = t();
  const deadline = deadlineText();
  show(`
    <section class="landing">
      ${sharedRoleTeaser()}
      <div class="opening">
        <p class="whos-there" tabindex="-1" data-focus>${ui.whosThere}</p>
        <p class="opening-source">${ui.whosThereSource}</p>
      </div>

      <p class="landing-intro">
        ${ui.landingIntro}
      </p>

      <ul class="merits">
        ${ui
          .merits(deadline)
          .map(
            (m) => `
        <li>
          <span class="merit-icon" aria-hidden="true">${m.icon}</span>
          <div><strong>${m.title}</strong>${m.text}</div>
        </li>`,
          )
          .join("")}
      </ul>

      <form class="register" id="register" data-form="register" novalidate>
        <h2>${ui.registerTitle}</h2>
        <label class="field">
          <span>${ui.email}</span>
          <input name="email" type="email" class="text-input" required maxlength="254"
            autocomplete="email" inputmode="email" placeholder="you@example.com" />
        </label>
        <label class="field">
          <span>${ui.nickname} <small>${ui.nicknameNote}</small></span>
          <input name="nickname" class="text-input" required maxlength="${NICKNAME_MAX}"
            autocomplete="nickname" placeholder="${ui.nicknamePlaceholder}" />
        </label>
        ${
          errorMessage
            ? `<div class="form-error" role="alert">
                 <p>${escapeHtml(errorMessage)}</p>
                 ${
                   errorCode === "email_taken"
                     ? `<p>${ui.takenLoginHint}</p>
                        <button type="button" class="btn btn-secondary" data-action="login-for-taken">${ui.takenLoginButton}</button>`
                     : ""
                 }
               </div>`
            : ""
        }
        <button type="submit" class="btn btn-primary">${ui.getCode}</button>
        <p class="form-note">${ui.registerNote(deadline)}</p>
      </form>

      <p class="login-entry">
        ${ui.loginEntry} <button type="button" class="link" data-action="show-login">${ui.loginEntryLink}</button>
      </p>
    </section>`);
  const form = app.querySelector<HTMLFormElement>("[data-form=register]")!;
  form.email.value = values.email;
  form.nickname.value = values.nickname;
}

// ===== 1-2. ログイン (別の端末から) =====

function renderLogin(errorMessage?: string, email = "", errorCode?: string): void {
  const ui = t();
  show(`
    <section class="login">
      <form class="register" data-form="login">
        <h1 class="login-title" tabindex="-1" data-focus>${ui.login}</h1>
        <p class="form-note">${ui.loginNote}</p>
        <label class="field">
          <span>${ui.email}</span>
          <input name="email" type="email" class="text-input" required maxlength="254"
            autocomplete="email" inputmode="email" placeholder="you@example.com" />
        </label>
        ${
          errorMessage
            ? `<div class="form-error" role="alert">
                 <p>${escapeHtml(errorMessage)}</p>
                 ${
                   errorCode === "not_registered"
                     ? `<button type="button" class="btn btn-secondary" data-action="signup-for-unknown">${ui.signupWithThis}</button>`
                     : ""
                 }
               </div>`
            : ""
        }
        <button type="submit" class="btn btn-primary">${ui.getCode}</button>
      </form>
      <p class="login-entry"><button type="button" class="link" data-action="show-landing">${ui.backToSignup}</button></p>
    </section>`);
  app.querySelector<HTMLFormElement>("[data-form=login]")!.email.value = email;
}

/** 確認コードを待っている登録・ログイン */
let pendingAuth: { mode: "signup" | "login"; email: string; nickname?: string } | null = null;

function renderCode(errorMessage?: string, notice?: string): void {
  const p = pendingAuth!;
  const ui = t();
  show(`
    <section class="login">
      <form class="register code-form" data-form="code" novalidate>
        <h1 class="login-title" tabindex="-1" data-focus>${ui.codeTitle}</h1>
        <p>${ui.codeSent(escapeHtml(p.email))}</p>
        <p class="spam-hint">${ui.spamHint}</p>
        <label class="field">
          <span>${ui.codeLabel}</span>
          <input name="code" class="text-input code-input" required inputmode="numeric"
            autocomplete="one-time-code" maxlength="12" placeholder="123456" />
        </label>
        ${errorMessage ? `<p class="form-error" role="alert">${escapeHtml(errorMessage)}</p>` : ""}
        ${notice ? `<p class="form-ok" role="status">${escapeHtml(notice)}</p>` : ""}
        <button type="submit" class="btn btn-primary">${p.mode === "signup" ? ui.completeSignup : ui.doLogin}</button>
        <p class="form-note">${ui.codeNote}</p>
        <div class="code-actions">
          <button type="button" class="link" data-action="resend-code">${ui.resendCode}</button>
          <button type="button" class="link" data-action="back-to-start">${ui.reenterEmail}</button>
        </div>
      </form>
    </section>`);
}

/** 確認コードを送る (新規登録・ログイン共通)。送れたらコード入力の画面へ */
async function sendCode(): Promise<void> {
  const p = pendingAuth!;
  if (p.mode === "signup") await api.signupStart(p.email, p.nickname!);
  else await api.loginStart(p.email);
}

// ===== 2. プロスペローの案内 =====

function renderWelcome(): void {
  const c = content();
  const ui = t();
  show(`
    <section class="welcome">
      <button type="button" class="btn btn-primary welcome-start-top" data-action="start">${ui.start}</button>
      <div class="figure-frame">${figure("prospero")}</div>
      <p class="speaker">${ui.guide(c.prospero.name, c.playTitle(c.prospero.play))}</p>
      <h1 class="welcome-title" tabindex="-1" data-focus>${ui.welcomeTitle}</h1>
      <div class="welcome-text">
        ${ui
          .welcomeText(escapeHtml(me!.nickname))
          .map((p) => `<p>${p}</p>`)
          .join("")}
      </div>
      ${quoteHtml(c.prosperoQuote)}
      <button type="button" class="btn btn-primary" data-action="start">${ui.start}</button>
    </section>`);
}

// ===== 3. 12問 =====

let answers: number[] = [];

function saveAnswers(): void {
  try {
    sessionStorage.setItem(ANSWERS_KEY, JSON.stringify(answers));
  } catch {
    // 保存できなくても診断は続けられる
  }
}

function loadAnswers(): number[] {
  try {
    const saved = JSON.parse(sessionStorage.getItem(ANSWERS_KEY) ?? "[]");
    return Array.isArray(saved) ? saved.slice(0, QUESTION_COUNT - 1) : [];
  } catch {
    return [];
  }
}

function renderQuestion(): void {
  const i = answers.length;
  const q = content().questions[i];
  const total = QUESTION_COUNT;
  show(`
    <section class="question">
      <div class="progress" role="progressbar" aria-valuemin="0" aria-valuemax="${total}"
        aria-valuenow="${i}" aria-label="${t().progressLabel(i + 1, total)}">
        <div class="progress-fill" style="width:${(i / total) * 100}%"></div>
      </div>
      <p class="question-count">${i + 1} <span>/ ${total}</span></p>
      <h1 class="question-text" tabindex="-1" data-focus>${q.text}</h1>
      <div class="choices">
        ${q.choices
          .map(
            (text, n) => `
          <button type="button" class="choice" data-choice="${n}">
            <span class="choice-label">${CHOICE_LABELS[n]}</span>
            <span>${text}</span>
          </button>`,
          )
          .join("")}
      </div>
      <button type="button" class="back" data-action="back" ${i === 0 ? "disabled" : ""}>${t().previousQuestion}</button>
    </section>`);
}

async function finishQuiz(): Promise<void> {
  show(`
    <section class="casting-wait">
      <p tabindex="-1" data-focus>${t().casting}</p>
    </section>`);
  try {
    const [updated] = await Promise.all([api.diagnose(answers), new Promise((r) => setTimeout(r, 1600))]);
    me = updated;
    answers = [];
    saveAnswers();
    updateChip();
    location.hash = "#role";
    await renderRole(true);
  } catch (e) {
    show(`
      <section class="casting-wait">
        <p class="form-error" role="alert" tabindex="-1" data-focus>${t().saveFailed(escapeHtml((e as Error).message))}</p>
        <button type="button" class="btn btn-primary" data-action="retry-diagnosis">${t().retry}</button>
      </section>`);
  }
}

// ===== 4. 配役発表 =====

/** 診断結果の下で紹介する本 (『一気読みシェイクスピア』) の買える場所 */
const BOOK_LINKS = [
  { store: "amazon", url: "https://www.amazon.co.jp/dp/4479394869" },
  { store: "rakuten", url: "https://books.rakuten.co.jp/rk/ba4eb096809a375081ac76644382f60e/" },
  { store: "publisher", url: "https://www.daiwashobo.co.jp/book/b10171192.html" },
] as const;

const myRole = (): Role => content().role(me!.diagnosis!.roleId)!;

async function renderRole(justCast = false): Promise<void> {
  const c = content();
  const ui = t();
  const role = myRole();
  const costar = c.role(role.costar.id)!;
  const same = role.front === role.back;
  show(`
    <article class="role ${justCast ? "is-reveal" : ""}">
      ${roleNav("role")}
      <header class="role-head">
        <p class="role-label">${ui.yourRoleIs}</p>
        <h1 class="role-name" tabindex="-1" data-focus>${role.name}</h1>
        <p class="role-play">${c.playTitle(role.play)}</p>
        <div class="figure-frame is-large">${figure(role.id)}</div>
        <p class="role-types">
          <span>${c.inPublic(c.types[role.front].label)}</span>×<span>${c.alone(c.types[role.back].label)}</span>
        </p>
        ${same ? `<p class="badge">${ui.noMask}</p>` : ""}
      </header>

      <div class="casting-text">${castingLines(c, role)
        .map((line) => `<p>${line}</p>`)
        .join("")}</div>

      ${quoteHtml(role.quote)}

      <section class="role-section">
        <h2>${ui.roleIntroTitle(role.name, role.play)}</h2>
        <p>${role.intro}</p>
      </section>

      <section class="role-section stats-line" id="same-count" aria-live="polite"></section>

      <section class="role-section costar">
        <h2>${ui.costarTitle}</h2>
        <div class="costar-body">
          <div class="figure-frame is-small">${figure(costar.id)}</div>
          <div>
            <p class="costar-name">${costar.name} <small>${c.playTitle(costar.play)}</small></p>
            <p class="costar-types">${typePairOf(c, costar.front, costar.back)}</p>
          </div>
        </div>
        <p>${role.costar.reason}</p>
      </section>

      <section class="role-section book">
        <h2>${ui.bookTitle}</h2>
        <div class="book-body">
          <img class="book-cover" src="/img/book/ikkiyomi-shakespeare.webp" width="600" height="868"
            alt="${getLocale() === "ja" ? "一気読みシェイクスピア（木村龍之介）" : "Ikkiyomi Shakespeare by Ryunosuke Kimura"}" loading="lazy" />
          <p class="book-text">
            <span>${ui.bookText(role.play)}</span>
            <small>${ui.bookBy}</small>
          </p>
        </div>
        <a class="book-main" href="${BOOK_LINKS[0].url}" target="_blank" rel="noopener">${ui.bookMain} ↗</a>
        <p class="book-others">${ui.bookOthers}${BOOK_LINKS.slice(1)
          .map((l) => `<a href="${l.url}" target="_blank" rel="noopener">${ui.bookLink[l.store]}</a>`)
          .join("・")}</p>
      </section>

      ${shareButtonsHtml(role)}

      <div class="actions">
        <button type="button" class="btn btn-primary" data-action="card-image">${ui.saveCard}</button>
        <a class="btn btn-secondary" href="#card">${ui.viewCard}</a>
      </div>

      <details class="about">
        <summary>${ui.aboutTitle}</summary>
        ${ui.about.map((p) => `<p>${p}</p>`).join("")}
      </details>

      <p class="retake"><button type="button" class="link" data-action="retake">${ui.retake}</button></p>
    </article>`);

  api
    .stats()
    .then((s) => {
      const n = s.roles[role.id] ?? 1;
      const el = app.querySelector("#same-count");
      // 自分ひとりのときは人数ではなく「あなただけ」と伝える
      if (el) el.innerHTML = `<p>${n <= 1 ? ui.onlyYou(role.name, s.diagnosed) : ui.sameCount(role.name, n, s.diagnosed)}</p>`;
    })
    .catch(() => undefined);
}

function roleNav(current: "role" | "card"): string {
  const ui = t();
  return `
    <nav class="tabs" aria-label="${ui.memberMenu}">
      <a href="#role" ${current === "role" ? 'aria-current="page"' : ""}>${ui.tabRole}</a>
      <a href="#card" ${current === "card" ? 'aria-current="page"' : ""}>${ui.tabCard}</a>
    </nav>`;
}

// ===== 5. 会員証 =====

function renderCard(saved?: "saved" | string): void {
  const m = me!;
  const c = content();
  const ui = t();
  const role = myRole();
  show(`
    <section class="member">
      ${roleNav("card")}
      <h1 class="visually-hidden" tabindex="-1" data-focus>${ui.tabCard}</h1>
      <div class="member-card">
        <div class="member-card-head">
          <p class="brand">BARD CLUB</p>
        </div>
        ${m.founding ? `<img class="founding-badge" src="/img/founding.svg" width="200" height="200" alt="">` : ""}
        <div class="member-card-body">
          <div class="figure-frame is-small">${figure(role.id)}</div>
          <div>
            <p class="member-no">${memberNo(m.number)}</p>
            ${m.founding ? `<p class="founding">${ui.founding}</p>` : ""}
            <p class="member-name">${escapeHtml(m.nickname)}</p>
            <p class="member-role">${role.name}<small>${c.playTitle(role.play)}</small></p>
          </div>
        </div>
        <dl class="member-meta">
          <div><dt>${ui.registeredOn}</dt><dd>${formatDate(m.createdAt)}</dd></div>
          <div><dt>${ui.castOn}</dt><dd>${formatDate(m.diagnosis!.diagnosedAt)}</dd></div>
        </dl>
      </div>
      <p class="member-note">${ui.memberNote}</p>

      <section class="reward">
        <p class="lock" aria-hidden="true">🔒</p>
        <h2>${ui.secretTitle}</h2>
        <p>${ui.secretText}</p>
      </section>

      <form class="member-section" data-form="message">
        <h2>${ui.messageTitle} <small>${ui.optional}</small></h2>
        <p class="form-note">${ui.messageNote}</p>
        <div class="field-row">
          <label class="visually-hidden" for="message">${ui.messageTitle}</label>
          <input id="message" name="message" class="text-input" maxlength="${MESSAGE_MAX}"
            placeholder="${ui.messagePlaceholder}" />
          <button type="submit" class="btn btn-secondary">${ui.save}</button>
        </div>
        ${
          saved === "saved"
            ? `<p class="form-ok" role="status">${ui.saved}</p>`
            : saved
              ? `<p class="form-error" role="alert">${escapeHtml(saved)}</p>`
              : ""
        }
      </form>

      <section class="member-section">
        <h2>${ui.accountTitle}</h2>
        <dl class="member-info">
          <div><dt>${ui.email}</dt><dd>${escapeHtml(m.email)}${
            m.emailVerified ? `<span class="verified">${ui.verified}</span>` : `<span class="unverified">${ui.unverified}</span>`
          }</dd></div>
        </dl>

        <p class="form-note">${ui.accountNote(deadlineText())}</p>
      </section>

      <p class="logout"><button type="button" class="link" data-action="logout">${ui.logout}</button></p>
    </section>`);
  app.querySelector<HTMLInputElement>("#message")!.value = m.message;
}

// ===== 画面の切り替え =====

async function route(): Promise<void> {
  if (!me) return renderLanding();
  if (!me.diagnosis) {
    answers = loadAnswers();
    return answers.length ? renderQuestion() : renderWelcome();
  }
  if (location.hash === "#card") return renderCard();
  if (location.hash === "#quiz") {
    answers = loadAnswers();
    return renderQuestion();
  }
  return renderRole();
}

/** 言語を切り替えたら、いまの画面をその言語で描き直す (入力中の値は残す) */
function rerender(): void {
  const register = app.querySelector<HTMLFormElement>("[data-form=register]");
  const login = app.querySelector<HTMLFormElement>("[data-form=login]");
  const code = app.querySelector<HTMLFormElement>("[data-form=code]");
  if (register) {
    renderLanding(undefined, {
      email: String(register.email.value),
      nickname: String(register.nickname.value),
    });
  } else if (login) {
    renderLogin(undefined, String(login.email.value));
  } else if (code && pendingAuth) {
    const typed = String(code.code.value);
    renderCode();
    app.querySelector<HTMLFormElement>("[data-form=code]")!.code.value = typed;
  } else {
    void route();
  }
}

window.addEventListener("hashchange", () => void route());

app.addEventListener("submit", async (event) => {
  event.preventDefault();
  const form = event.target as HTMLFormElement;
  const button = form.querySelector<HTMLButtonElement>("[type=submit]");

  if (form.dataset.form === "register") {
    const email = String(form.email.value).trim();
    const nickname = String(form.nickname.value).trim();
    if (!email || !nickname) return renderLanding(t().enterEmailAndNickname, { email, nickname });
    if (button) button.disabled = true;
    pendingAuth = { mode: "signup", email, nickname };
    try {
      await sendCode();
      renderCode();
    } catch (e) {
      renderLanding((e as Error).message, { email, nickname }, (e as { code?: string }).code);
    }
  }

  if (form.dataset.form === "login") {
    const email = String(form.email.value).trim();
    if (!email) return renderLogin(t().enterEmail);
    if (button) button.disabled = true;
    pendingAuth = { mode: "login", email };
    try {
      await sendCode();
      renderCode();
    } catch (e) {
      renderLogin((e as Error).message, email, (e as { code?: string }).code);
    }
  }

  if (form.dataset.form === "code" && pendingAuth) {
    const code = String(form.code.value).trim();
    if (!code) return renderCode(t().enterCode);
    if (button) button.disabled = true;
    const p = pendingAuth;
    try {
      me = p.mode === "signup" ? await api.signupVerify(p.email, code) : await api.loginVerify(p.email, code);
      pendingAuth = null;
      updateChip();
      await route();
    } catch (e) {
      // 登録の途中で、同じアダ名やメールアドレスが先に登録されてしまったときは最初の画面に戻す
      const errCode = (e as { code?: string }).code;
      if (errCode === "nickname_taken" || errCode === "email_taken" || errCode === "no_pending") {
        renderLanding((e as Error).message, { email: p.email, nickname: p.nickname ?? "" }, errCode);
      } else {
        renderCode((e as Error).message);
      }
    }
  }

  if (form.dataset.form === "message") {
    if (button) button.disabled = true;
    try {
      me = await api.updateMessage(String(form.message.value));
      renderCard("saved");
    } catch (e) {
      renderCard((e as Error).message);
    }
  }
});

app.addEventListener("click", async (event) => {
  const target = event.target as HTMLElement;

  const shareId = target.closest<HTMLElement>("[data-share]")?.dataset.share;
  if (shareId && me?.diagnosis) {
    await handleShare(shareId, myRole(), me, app.querySelector(".share-status"));
    return;
  }

  const choice = target.closest<HTMLButtonElement>("[data-choice]");
  if (choice) {
    // 連打や押しまちがいで2問進まないよう、すぐにボタンを止める
    app.querySelectorAll<HTMLButtonElement>(".choice, .back").forEach((b) => (b.disabled = true));
    choice.classList.add("is-picked");
    answers.push(Number(choice.dataset.choice));
    saveAnswers();
    await new Promise((r) => setTimeout(r, 260));
    if (answers.length < QUESTION_COUNT) renderQuestion();
    else void finishQuiz();
    return;
  }

  const action = target.closest<HTMLElement>("[data-action]")?.dataset.action;
  if (action === "show-login") {
    renderLogin();
    return;
  }
  if (action === "show-landing") {
    renderLanding();
    return;
  }
  if (action === "signup-for-unknown") {
    // ログインで入れたメールアドレスを、新規登録の欄に入れておく
    const email = String(app.querySelector<HTMLFormElement>("[data-form=login]")?.email.value ?? "").trim();
    renderLanding(undefined, { email, nickname: "" });
    app.querySelector<HTMLElement>("#register")?.scrollIntoView();
    app.querySelector<HTMLInputElement>("[data-form=register] [name=nickname]")?.focus();
    return;
  }
  if (action === "login-for-taken") {
    const email = String(app.querySelector<HTMLFormElement>("[data-form=register]")?.email.value ?? "").trim();
    pendingAuth = { mode: "login", email };
    try {
      await sendCode();
      renderCode();
    } catch (e) {
      renderLogin((e as Error).message, email);
    }
    return;
  }
  if (action === "resend-code" && pendingAuth) {
    try {
      await sendCode();
      renderCode(undefined, t().codeResent);
    } catch (e) {
      renderCode((e as Error).message);
    }
    return;
  }
  if (action === "back-to-start") {
    const p = pendingAuth;
    pendingAuth = null;
    if (p?.mode === "signup") renderLanding(undefined, { email: p.email, nickname: p.nickname ?? "" });
    else renderLogin(undefined, p?.email ?? "");
    return;
  }
  if (action === "logout") {
    if (!confirm(t().logoutConfirm)) return;
    await api.logout().catch(() => undefined);
    me = null;
    updateChip();
    location.hash = "";
    renderLanding();
    return;
  }
  if (action === "start") {
    answers = [];
    saveAnswers();
    renderQuestion();
  } else if (action === "back") {
    answers.pop();
    saveAnswers();
    renderQuestion();
  } else if (action === "retry-diagnosis") {
    void finishQuiz();
  } else if (action === "retake") {
    if (confirm(t().retakeConfirm)) {
      answers = [];
      saveAnswers();
      location.hash = "#quiz";
    }
  } else if (action === "card-image") {
    const button = target.closest<HTMLButtonElement>("button")!;
    const role = myRole();
    const c = content();
    button.disabled = true;
    button.textContent = t().makingImage;
    try {
      const blob = await renderCardImage(role, me!);
      showCardPreview(blob, `bard-club-${role.id}.png`, `${c.share.title(role)} #BardClub`);
      button.textContent = t().saveCard;
    } catch {
      button.textContent = t().imageFailed;
    }
    button.disabled = false;
  }
});

// ===== 言語の切り替え =====

function setupLangSelect(): void {
  if (!langSelect) return;
  langSelect.setAttribute("aria-label", t().languageLabel);
  langSelect.innerHTML = LOCALES.map(
    (l) => `<option value="${l}" lang="${l}" ${l === getLocale() ? "selected" : ""}>${LOCALE_NAMES[l]}</option>`,
  ).join("");
  langSelect.addEventListener("change", () => {
    const next = langSelect.value;
    if (!isLocale(next)) return;
    setLocale(next);
    langSelect.setAttribute("aria-label", t().languageLabel);
    rerender();
    // ログイン中なら、これからのメールもこの言語で送るよう覚えてもらう
    if (me) void api.updateLang(next).catch(() => undefined);
  });
}

// ===== 起動 =====

function showMailNotice(): void {
  const params = new URLSearchParams(location.search);
  const notice = t().mailNotices[params.get("mail") ?? ""];
  if (!notice) return;
  // URL から ?mail=... を消して、再読み込みで同じお知らせが出ないようにする
  history.replaceState(null, "", location.pathname + location.hash);
  const el = document.createElement("p");
  el.className = "toast";
  el.setAttribute("role", "status");
  el.textContent = notice;
  document.body.append(el);
  setTimeout(() => el.remove(), 6000);
}

(async () => {
  applyLocale();
  setupLangSelect();
  showMailNotice();
  try {
    me = await api.me();
  } catch {
    me = null;
  }
  updateChip();
  await route();
})();
